import { format } from "date-fns";
import {
  Contact,
  EnumAddressLocationType,
  EnumEventAccessOperatorStatus,
  EnumEventLocationLocationInfoType,
  EnumEventLocationType,
  EnumEventRecurrencePatternDayOfMonth,
  EnumEventRecurrencePatternDaysOfWeek,
  EnumEventRecurrencePatternFrequency,
  EnumEventType,
  EventByIdQuery,
  EventLocation,
  useEventCreateMutation,
  useEventUpdateByIdMutation,
} from "gql/generated";
import { LocationListData } from "apps/events/types/LocationListData";
import i18n, { i18nLanguage, i18nNamespace, loadLanguagePacks } from "i18n";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormHandler } from "shared/hooks";
import {
  buildOperatorRequestContext,
  createMultilingual,
  EVENT_DATE_FORMAT,
  getAddressString,
  getMultilingualString,
  isValidUrl,
  phoneNumberRegex,
} from "shared/utils";
import { z } from "zod";

const getContactSchema = () =>
  z
    .object({
      fullAddress: z.string().optional(),
      address: z
        .object({
          line1: z.string().optional().default(""),
          line2: z.string().optional().default(""),
          city: z.string().optional().default(""),
          subdivision: z.string().optional().default(""),
          postalCode: z.string().optional().default(""),
          country: z.string().optional().default(""),
          location: z
            .object({
              type: z.string().default("Point"),
              coordinates: z.object({
                lat: z.number().min(-90).max(90),
                lng: z.number().min(-180).max(180),
              }),
            })
            .optional(),
        })
        .partial()
        .optional(),
      email: z
        .string()
        .optional()
        .refine(
          value => {
            if (value) {
              return z.string().email().safeParse(value).success;
            }
            return true;
          },
          {
            message: i18n.t("events:APP.MODAL.ERRORS.INVALID_EMAIL"),
          },
        ),
      phone: z
        .string()
        .optional()
        .refine(
          value => {
            if (value) {
              // Look for valid phone numbers
              return phoneNumberRegex.test(value);
            }
            return true;
          },
          {
            message: i18n.t("events:APP.MODAL.ERRORS.INVALID_PHONE_NUMBER"),
          },
        ),
      tollFreePublicPhone: z.string().optional(),
      links: z
        .object({
          website: z
            .string()
            .optional()
            .refine(
              value => {
                if (value) {
                  return isValidUrl(value);
                }
                return true;
              },
              {
                message: i18n.t("events:APP.MODAL.ERRORS.INVALID_URL"),
              },
            ),
          // These aren't currently used, so don't need real validation
          instagram: z.string().optional(),
          x: z.string().optional(),
          bookNow: z.string().optional(),
          youtube: z.string().optional(),
          pinterest: z.string().optional(),
          googleMyBusiness: z.string().optional(),
          tiktok: z.string().optional(),
          facebook: z.string().optional(),
          tripAdvisor: z.string().optional(),
        })
        .optional()
        .default({}),
    })
    .partial()
    .optional()
    .default({});

export type EventContactFormValues = z.infer<
  ReturnType<typeof getContactSchema>
>;

const getEventLocationSchema = () =>
  z
    .object({
      venue: z.string().optional().or(z.literal("")),
      contact: getContactSchema(),
      locationInfo: z
        .object({
          _entityId: z.string().optional(),
          type: z.nativeEnum(EnumEventLocationLocationInfoType).optional(),
        })
        .optional()
        .default({}),
    })
    .partial()
    .optional()
    .default({});

export type EventLocationFormValues = z.infer<
  ReturnType<typeof getEventLocationSchema>
>;

const getEventCreateEditFormSchema = () =>
  z
    .object({
      step: z.number().default(1),
      // Step 1
      createdByEmail: z
        .string({
          required_error: `${i18n.t("events:APP.MODAL.EVENT_DETAILS.LABELS.EVENT_NAME")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
        })
        .email({
          message: i18n.t("events:APP.MODAL.ERRORS.INVALID_EMAIL"),
        }),
      createdByFirstName: z
        .string({
          required_error: `${i18n.t("events:APP.MODAL.EVENT_DETAILS.LABELS.USER_FIRST_NAME")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
        })
        .trim()
        .min(1, {
          message: `${i18n.t("events:APP.MODAL.EVENT_DETAILS.LABELS.USER_FIRST_NAME")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
        }),
      createdByLastName: z
        .string({
          required_error: `${i18n.t("events:APP.MODAL.EVENT_DETAILS.LABELS.USER_LAST_NAME")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
        })
        .trim()
        .min(1, {
          message: `${i18n.t("events:APP.MODAL.EVENT_DETAILS.LABELS.USER_LAST_NAME")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
        }),
      eventOrganizer: getEventLocationSchema().superRefine((data, ctx) => {
        if (!data.venue?.trim()) {
          // This will show an error below the Combobox
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${i18n.t("events:APP.MODAL.EVENT_DETAILS.LABELS.EVENT_ORGANIZER")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
          });
          // This will show an error in the ControlledInput
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${i18n.t("events:APP.MODAL.EVENT_DETAILS.LABELS.EVENT_ORGANIZER")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
            path: ["venue"],
          });
        }
      }),
      locationType: z.nativeEnum(EnumEventLocationType),
      title: z
        .string({
          required_error: `${i18n.t("events:APP.MODAL.EVENT_DETAILS.LABELS.EVENT_NAME")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
        })
        .trim()
        .min(1, {
          message: `${i18n.t("events:APP.MODAL.EVENT_DETAILS.LABELS.EVENT_NAME")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
        }),
      description: z
        .string({
          required_error: `${i18n.t("events:APP.MODAL.EVENT_DETAILS.LABELS.EVENT_DESCRIPTION")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
        })
        .trim()
        .min(1, {
          message: `${i18n.t("events:APP.MODAL.EVENT_DETAILS.LABELS.EVENT_DESCRIPTION")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
        }),
      price: z.string().optional().or(z.literal("")),
      ticketUrl: z
        .string()
        .url()
        .optional()
        .or(z.literal(""))
        .refine(
          value => {
            if (value) {
              return z.string().url().safeParse(value).success;
            }
            return true;
          },
          {
            message: i18n.t("events:APP.MODAL.ERRORS.INVALID_URL"),
          },
        ),
      tags: z.object({
        global: z.array(z.string()).optional().default([]),
        organizationSpecific: z.array(z.string()).optional().default([]),
      }),
      // Step 2
      eventLocations: z.array(getEventLocationSchema()).optional().default([]),
      access: z.object({
        operator: z
          .object({
            _entityId: z.string(),
          })
          .optional(),
        organizations: z
          .array(
            z.object({
              _entityId: z.string(),
              status: z.nativeEnum(EnumEventAccessOperatorStatus),
            }),
          )
          .default([]),
      }),
      // Step 3
      imagesToKeep: z.array(z.string()).optional().default([]),
      // Step 4
      type: z.nativeEnum(EnumEventType),
      startDate: z.string(),
      endDate: z.string().optional(),
      schedules: z
        .array(
          z.object({
            allDay: z.boolean().optional().default(false),
            timeSlots: z
              .array(
                z
                  .object({
                    _id: z.string().optional(),
                    from: z.string(),
                    to: z.string(),
                    endsNextDay: z.boolean().optional().default(false),
                    note: z.string(),
                  })
                  .optional()
                  .refine(
                    data => {
                      // There needs to be either a startTime, endTime, or note
                      if (!data?.from && !data?.to && !data?.note) {
                        return false;
                      }
                      return true;
                    },
                    {
                      message: i18n.t(
                        "events:APP.MODAL.EVENT_SCHEDULE.TIME_SLOTS.ERROR",
                      ),
                      // We'll just show the error on the startTime
                      path: ["from"],
                    },
                  ),
              )
              .optional()
              .default([]),
          }),
        )
        .optional(),
      recurrencePattern: z.object({
        frequency: z.nativeEnum(EnumEventRecurrencePatternFrequency),
        interval: z.number().default(1).optional(),
        endDate: z.string().optional(),
        multiDates: z.array(z.string()).optional(),
        daysOfWeek: z
          .array(z.nativeEnum(EnumEventRecurrencePatternDaysOfWeek))
          .optional(),
        dateOfMonth: z.number().optional(),
        dayOfMonth: z
          .nativeEnum(EnumEventRecurrencePatternDayOfMonth)
          .optional(),
        dayOfMonthOccurrence: z.number().optional(),
        monthAndDateOfYear: z.string().optional(),
        skipDates: z.array(z.string()).optional(),
      }),
      // Step 5
      isSubmitting: z.boolean().default(false),
    })
    .superRefine((data, ctx) => {
      // On step 2, if the event is IN PERSON, we need to ensure that there is at least ONE location
      // AND that ALL locations have an address with coordinates
      if (data.step === 2) {
        if (data.locationType === EnumEventLocationType.InPerson) {
          if (data.eventLocations.length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: i18n.t(
                "events:APP.MODAL.EVENT_HOST.LOCATION_SELECTION.ERRORS.NO_LOCATIONS",
              ),
              path: ["step"],
            });
          }
          for (let i = 0; i < data.eventLocations.length; i++) {
            const location = data.eventLocations[i];
            // Show errors for invalid coordinates or address ONLY for step 2
            const hasCoords =
              !!location.contact?.address?.location?.coordinates;
            const hasLat =
              hasCoords &&
              location.contact?.address?.location?.coordinates.lat !== 0;
            const hasLng =
              hasCoords &&
              location.contact?.address?.location?.coordinates.lng !== 0;
            if (!hasLat || !hasLng) {
              const noAddressMessage = i18n.t(
                "events:APP.MODAL.EVENT_HOST.LOCATION_SELECTION.ERRORS.NO_ADDRESS",
              );
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: noAddressMessage,
                path: [`eventLocations.${i}`],
              });
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: noAddressMessage,
                path: [`eventLocations.${i}.contact.fullAddress`],
              });
            }
            if (!hasLat) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t(
                  i18n.t("core:SELECT_ADDRESS.LATITUDE") +
                    " " +
                    i18n.t("core:FORM_ERRORS.REQUIRED"),
                ),
                path: [
                  `eventLocations.${i}.contact.address.location.coordinates.lat`,
                ],
              });
            }
            if (!hasLng) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t(
                  i18n.t("core:SELECT_ADDRESS.LONGITUDE") +
                    " " +
                    i18n.t("core:FORM_ERRORS.REQUIRED"),
                ),
                path: [
                  `eventLocations.${i}.contact.address.location.coordinates.lng`,
                ],
              });
            }
          }
        }
      }
    });

export type EventCreateEditFormValues = z.infer<
  ReturnType<typeof getEventCreateEditFormSchema>
>;

/**
 * Get the default values for the contact form
 * @param contact - The contact object to get the default values for
 * @returns The default values for the contact form
 */
export const getContactDefaultValue = (contact: Contact | undefined) => ({
  fullAddress: contact?.address ? getAddressString(contact.address) : "",
  address: {
    line1: contact?.address?.line1 ? contact.address.line1 : "",
    line2: contact?.address?.line2 ? contact.address.line2 : "",
    city: contact?.address?.city ? contact.address.city : "",
    subdivision: contact?.address?.subdivision
      ? contact.address.subdivision
      : "",
    postalCode: contact?.address?.postalCode ? contact.address.postalCode : "",
    country: contact?.address?.country ? contact.address.country : "",
    location: {
      type: contact?.address?.location?.type
        ? contact.address.location?.type
        : "Point",
      coordinates: {
        lng: contact?.address?.location?.coordinates[0]
          ? contact.address.location.coordinates[0]
          : 0,
        lat: contact?.address?.location?.coordinates[1]
          ? contact.address.location.coordinates[1]
          : 0,
      },
    },
  },
  email: contact?.email ?? "",
  phone: contact?.phone ?? "",
  tollFreePublicPhone: contact?.tollFreePublicPhone ?? "",
  links: {
    website: contact?.links?.website ?? "",
    instagram: contact?.links?.instagram ?? "",
    x: contact?.links?.x ?? "",
    bookNow: contact?.links?.bookNow ?? "",
    youtube: contact?.links?.youtube ?? "",
    pinterest: contact?.links?.pinterest ?? "",
    googleMyBusiness: contact?.links?.googleMyBusiness ?? "",
    tiktok: contact?.links?.tiktok ?? "",
    facebook: contact?.links?.facebook ?? "",
    tripAdvisor: contact?.links?.tripAdvisor ?? "",
  },
});

/**
 * Get the default values for the event location form
 * @param location - The location object to get the default values for
 * @returns The default values for the event location form
 */
export const getEventLocationFormValues = (location: EventLocation) => ({
  venue: location?.venue ? getMultilingualString(location.venue) : "",
  contact: getContactDefaultValue(location?.contact ?? {}),
  locationInfo: {
    ...(location?.locationInfo?._entityId && {
      _entityId: location.locationInfo._entityId,
    }),
    ...(location?.locationInfo?.type && {
      type: location.locationInfo.type,
    }),
  },
});

/**
 * Check if the location is empty
 * - Only the venue name is required, so we'll just make sure that is present
 * @param location - The location to check
 * @returns True if the location is empty, false otherwise
 */
export const isEmptyLocation = (location: EventLocationFormValues) =>
  !location || !location.venue;

/**
 * Convert the form values to a contact object
 * @param values - The form values to convert
 * @returns The contact object
 */
export const convertContactFormValuesToContact = (
  values: EventContactFormValues,
) => ({
  email: values.email ?? "",
  phone: values.phone ?? "",
  links: {
    ...values.links,
  },
  address: {
    ...(values.address?.line1 && { line1: values.address.line1 }),
    ...(values.address?.line2 && { line2: values.address.line2 }),
    ...(values.address?.city && { city: values.address.city }),
    ...(values.address?.subdivision && {
      subdivision: values.address.subdivision,
    }),
    ...(values.address?.postalCode && {
      postalCode: values.address.postalCode,
    }),
    ...(values.address?.country && { country: values.address.country }),
    ...(values.address?.location &&
      values.address.location.coordinates.lng !== 0 &&
      values.address.location.coordinates.lat !== 0 && {
        location: {
          type: EnumAddressLocationType.Point,
          coordinates: [
            values.address.location.coordinates.lng ?? 0,
            values.address.location.coordinates.lat ?? 0,
          ],
        },
      }),
  },
});

/**
 * Convert the form values to an event location object
 * @param values - The form values to convert
 * @returns The event location object
 */
export const convertFormValuesToEventLocation = (
  values: EventLocationFormValues,
) => ({
  venue: createMultilingual(values.venue ?? ""),
  contact: convertContactFormValuesToContact(values.contact ?? {}),
  locationInfo: {
    ...(values.locationInfo?._entityId
      ? {
          _entityId: values.locationInfo._entityId,
        }
      : undefined),
    ...(values.locationInfo?.type
      ? {
          type: values.locationInfo.type,
        }
      : undefined),
  },
});

interface EventCreateEditFormOptions {
  event: EventByIdQuery["eventById"];
  defaultOrganizationId: string;
  /** Organization tag ids pre-applied to new events (e.g. from the submit widget's `tag-ids`). */
  defaultOrganizationTagIds?: string[];
  userEmailAddress: string;
  userFirstName: string;
  userLastName: string;
  /** True for operator users, including on pages (e.g. Home) whose URL has no operator segment. */
  isOperatorUser?: boolean;
  /** Known operator/organization listings, used to find the chosen host's own active enrollment on create. */
  allLocations?: LocationListData[];
  onSuccess?: (event: EventByIdQuery["eventById"]) => void;
  onError?: (message: string) => void;
}

export const useEventCreateEditForm = ({
  event,
  defaultOrganizationId,
  defaultOrganizationTagIds = [],
  userEmailAddress,
  userFirstName,
  userLastName,
  isOperatorUser,
  allLocations,
  onSuccess,
  onError,
}: EventCreateEditFormOptions) => {
  const defaultValues: EventCreateEditFormValues = {
    step: 1,
    // Step 1
    createdByEmail: event?.createdByEmail ?? userEmailAddress ?? "",
    createdByFirstName: event?.createdByFirstName ?? userFirstName ?? "",
    createdByLastName: event?.createdByLastName ?? userLastName ?? "",
    eventOrganizer: getEventLocationFormValues(
      event?.eventOrganizer ?? ({} as EventLocation),
    ),
    locationType: event?.locationType ?? EnumEventLocationType.InPerson,
    title: event?.title ? getMultilingualString(event.title) : "",
    description: event?.description
      ? getMultilingualString(event.description)
      : "",
    price: event?.price ?? "",
    ticketUrl: event?.ticketUrl ?? "",
    tags: event?.tags
      ? {
          global: event.tags.global?.map(tag => tag._id) ?? [],
          organizationSpecific:
            event.tags.organizationSpecific?.map(tag => tag._id) ?? [],
        }
      : {
          global: [],
          organizationSpecific: [...defaultOrganizationTagIds],
        },
    // Step 2
    eventLocations:
      event?.eventLocations?.map(location =>
        getEventLocationFormValues(location ?? ({} as EventLocation)),
      ) ?? [],
    access: {
      operator: event?.access.operator
        ? {
            _entityId: event.access.operator._entityId,
          }
        : undefined,
      organizations:
        event?.access.organizations.map(organization => ({
          _entityId: organization?._entityId ?? "",
          status: organization?.status ?? EnumEventAccessOperatorStatus.Draft,
        })) ?? [],
    },
    // Step 3
    imagesToKeep: event?.images?.map(image => image._id) ?? [],
    // Step 4
    type: event?.type ?? EnumEventType.Single,
    startDate: event?.startDate ?? format(new Date(), EVENT_DATE_FORMAT),
    endDate: event?.endDate ?? undefined,
    schedules: event?.schedules?.map(schedule => ({
      allDay: schedule?.allDay ?? false,
      timeSlots:
        schedule?.timeSlots
          ?.map(slot => ({
            _id: slot?._id ?? undefined,
            from: slot?.from ?? "",
            to: slot?.to ?? "",
            endsNextDay: slot?.endsNextDay ?? false,
            note: slot?.note ?? "",
          }))
          .filter(slot => !!slot) ?? [],
    })) ?? [
      {
        allDay: false,
        timeSlots: [],
      },
    ],
    recurrencePattern: {
      frequency:
        event?.recurrencePattern?.frequency ??
        EnumEventRecurrencePatternFrequency.Once,
      ...(event?.recurrencePattern?.interval && {
        interval: event.recurrencePattern.interval,
      }),
      ...(event?.recurrencePattern?.endDate && {
        endDate: event.recurrencePattern.endDate as string,
      }),
      ...(event?.recurrencePattern?.multiDates && {
        multiDates: event.recurrencePattern.multiDates as string[],
      }),
      ...(event?.recurrencePattern?.daysOfWeek && {
        daysOfWeek: event.recurrencePattern
          .daysOfWeek as EnumEventRecurrencePatternDaysOfWeek[],
      }),
      ...(event?.recurrencePattern?.dateOfMonth && {
        dateOfMonth: event.recurrencePattern.dateOfMonth as number,
      }),
      ...(event?.recurrencePattern?.dayOfMonth && {
        dayOfMonth: event.recurrencePattern
          .dayOfMonth as EnumEventRecurrencePatternDayOfMonth,
      }),
      ...(event?.recurrencePattern?.dayOfMonthOccurrence && {
        dayOfMonthOccurrence: event.recurrencePattern
          .dayOfMonthOccurrence as number,
      }),
      ...(event?.recurrencePattern?.monthAndDateOfYear && {
        monthAndDateOfYear: event.recurrencePattern
          .monthAndDateOfYear as string,
      }),
      ...(event?.recurrencePattern?.skipDates && {
        skipDates: event.recurrencePattern.skipDates as string[],
      }),
    },
    isSubmitting: false,
  };

  /**
   * Instead of using a const schema, we need to use a state variable
   * because the schema is dynamically updated when the language changes
   * - This will load the "en" schema initially, and then if the language
   *   changes, will load the schema of the new language
   */
  const [schema, setSchema] = useState<
    ReturnType<typeof getEventCreateEditFormSchema>
  >(getEventCreateEditFormSchema());

  /**
   * Dynamically update the schema when the language changes
   * - When the language is changed, we need to reload the schema to update the
   *   error messages to the new language
   */
  const { i18n } = useTranslation();
  useEffect(() => {
    const loadLanguagePacksAndUpdateSchemas = async () => {
      await loadLanguagePacks(
        [i18nNamespace.Core, i18nNamespace.Events],
        "event-submit",
        i18n.language as i18nLanguage,
      );
      setSchema(getEventCreateEditFormSchema());
    };
    loadLanguagePacksAndUpdateSchemas();
  }, [i18n.language, setSchema]);

  const [createEvent] = useEventCreateMutation();
  const [updateEvent] = useEventUpdateByIdMutation();

  return useFormHandler<EventCreateEditFormValues>(
    useCallback(
      async (data, formState) => {
        // isSubmitting alone must always reach the server as an explicit
        // submit, even when no other field is dirty (e.g. clicking Submit
        // immediately after opening an already-complete draft).
        if (
          data &&
          (Object.keys(formState.dirtyFields).length > 0 || data.isSubmitting)
        ) {
          if (event && event._id) {
            const fieldsToUpdate = Object.keys(formState.dirtyFields).reduce(
              (obj, key) => ({
                ...obj,
                ...{
                  [key]:
                    ["title", "description"].indexOf(key) > -1
                      ? createMultilingual(
                          data[key as "title" | "description"] ?? "",
                        )
                      : key === "eventOrganizer"
                        ? convertFormValuesToEventLocation(
                            data.eventOrganizer as EventLocationFormValues,
                          )
                        : key === "eventLocations"
                          ? // Drop unfilled placeholder rows. Opening the
                            // "add location" modal appends a blank entry
                            // immediately, and the step-2 guard that would
                            // reject it only runs while step === 2 — so
                            // without this a stray blank row gets saved when
                            // the user saves from a later step.
                            (data.eventLocations
                              ?.filter(
                                location =>
                                  !isEmptyLocation(
                                    location as EventLocationFormValues,
                                  ),
                              )
                              .map(location =>
                                convertFormValuesToEventLocation(
                                  location as EventLocationFormValues,
                                ),
                              ) ?? [])
                          : data[key as keyof EventCreateEditFormValues],
                },
              }),
              {},
            );
            // If we're adding/deleting images, we need to remove them from the fieldsToUpdate object
            // and pass them as separate variables to the updateEvent mutation
            const imagesToKeepWasDirty = Object.prototype.hasOwnProperty.call(
              formState.dirtyFields,
              "imagesToKeep",
            );
            const imagesToKeep =
              (fieldsToUpdate as EventCreateEditFormValues).imagesToKeep ?? [];

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            delete (fieldsToUpdate as any).imagesToKeep;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            delete (fieldsToUpdate as any).isSubmitting;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            delete (fieldsToUpdate as any).step;

            updateEvent({
              variables: {
                id: event._id,
                record: fieldsToUpdate,
                ...(imagesToKeepWasDirty && { imagesToKeep }),
                ...(data.isSubmitting && { isSubmitting: true }),
              },
              ...(isOperatorUser && {
                context: buildOperatorRequestContext(
                  event.access.operator?._entityId,
                ),
              }),
            })
              .then(result => {
                if (!result.errors && result.data?.eventUpdateById?.record) {
                  onSuccess?.(result.data.eventUpdateById.record);
                } else {
                  onError?.(result.errors?.[0]?.message ?? "An error occurred");
                }
              })
              .catch(() => {
                onError?.("");
              });
          } else {
            const defaultDate = format(new Date(), EVENT_DATE_FORMAT);
            const fieldsToCreate = {
              eventOrganizer: convertFormValuesToEventLocation(
                data.eventOrganizer as EventLocationFormValues,
              ),
              title: createMultilingual(data.title ?? ""),
              description: createMultilingual(data.description ?? ""),
              // NOTE: Removed venue, contact, eventLocation, and otherLocations fields
              // as they are all deprecated
              // NOTE: createdBy is fetched on the server based on createdByEmail
              createdByEmail: data.createdByEmail ?? "",
              createdByFirstName: data.createdByFirstName ?? "",
              createdByLastName: data.createdByLastName ?? "",
              locationType: data.locationType ?? EnumEventLocationType.InPerson,
              price: data.price ?? "",
              ticketUrl: data.ticketUrl ?? "",
              tags: {
                global: data.tags?.global ?? [],
                organizationSpecific: data.tags?.organizationSpecific ?? [],
              },
              // NOTE: This will be updated in step 2
              eventLocations: [],
              // NOTE: This will be updated in step 3
              images: [],
              // NOTE: These will be updated in step 4
              startDate: defaultDate,
              endDate: defaultDate,
              type: EnumEventType.Single,
              schedules: [
                {
                  allDay: false,
                  timeSlots: [],
                },
              ],
              recurrencePattern: {
                frequency: EnumEventRecurrencePatternFrequency.Once,
              },
              // NOTE: Access is now set based on the event organizer,
              // so it should exist in the form values
              access: {
                ...(data.access?.operator
                  ? {
                      operator: {
                        _entityId: data.access.operator._entityId,
                      },
                    }
                  : undefined),
                // Single creating org only until submit; backend adds other
                // enrollments on submit. Prefer the chosen host's own active
                // enrollment (falling back to defaultOrganizationId, e.g. on
                // Home before enrollments have loaded) so the draft's initial
                // org isn't an unrelated org from a different operator.
                organizations: [
                  {
                    _entityId:
                      allLocations
                        ?.find(
                          location =>
                            location.value === data.access?.operator?._entityId,
                        )
                        ?.data.enrollments.find(e => e.isActive)
                        ?.organizationId ?? defaultOrganizationId,
                    status: EnumEventAccessOperatorStatus.Draft,
                  },
                ],
              },
            };

            createEvent({
              variables: { record: fieldsToCreate },
              ...(isOperatorUser && {
                context: buildOperatorRequestContext(
                  data.access?.operator?._entityId,
                ),
              }),
            })
              .then(result => {
                if (!result.errors && result.data?.eventCreate?.record) {
                  onSuccess?.(result.data.eventCreate.record);
                } else {
                  onError?.(result.errors?.[0]?.message ?? "An error occurred");
                }
              })
              .catch(() => {
                onError?.("");
              });
          }
        }
      },
      [
        createEvent,
        updateEvent,
        onSuccess,
        onError,
        defaultOrganizationId,
        event,
        isOperatorUser,
        allLocations,
      ],
    ),
    defaultValues,
    { schema, mode: "onBlur" },
  );
};
